import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, CheckCircle2, ArrowRight, ChevronRight } from "lucide-react";
import HouseholdNav from "@/components/HouseholdNav";

const STEPS = [
    { key: "invite",       label: "Invite household",           to: "/household",            done: false },
    { key: "ownership",    label: "Set record ownership",       to: "/household",            done: false },
    { key: "privacy",      label: "Choose privacy preference",  to: "/household",            done: false },
    { key: "obligations",  label: "Add shared obligations",     to: "/bills",                done: false },
    { key: "goal",         label: "Create first shared goal",   to: "/household/goals",      done: false },
    { key: "brief",        label: "Configure Household Brief",  to: "/settings/notifications", done: false },
];

const KEY = "vault.household_onboarding.done";

function loadDone() {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
}
function saveDone(arr) { localStorage.setItem(KEY, JSON.stringify(arr)); }

export default function HouseholdOnboarding() {
    const [done, setDone] = useState(loadDone());

    const mark = (k) => {
        const next = done.includes(k) ? done.filter(x => x !== k) : [...done, k];
        setDone(next); saveDone(next);
    };
    const skipAll = () => { setDone(STEPS.map(s => s.key)); saveDone(STEPS.map(s => s.key)); };

    const progressPct = Math.round((done.length / STEPS.length) * 100);

    return (
        <div className="max-w-3xl mx-auto p-6 md:p-8" data-testid="household-onboarding-page">
            <HouseholdNav />
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                    <Sparkles size={12} /> Household onboarding
                </div>
                <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">
                    Get your Family VAULT organised in six calm steps.
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    Every step is optional and can be picked up later — VAULT never blocks you on any of them.
                </p>
            </div>

            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden mb-4">
                <div className="px-4 py-3 flex items-center gap-2 border-b border-stone-100">
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Progress</div>
                    <div className="flex-1 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                        <div className="h-full bg-stone-900 transition-all" style={{ width: `${progressPct}%` }} />
                    </div>
                    <div className="text-[11px] text-stone-500 tabular-nums" data-testid="onboarding-progress">{progressPct}%</div>
                </div>
                {STEPS.map((s, i) => {
                    const isDone = done.includes(s.key);
                    return (
                        <div key={s.key} className="border-b border-stone-100 last:border-0 px-4 py-3 flex items-center gap-3"
                             data-testid={`onboarding-step-${s.key}`}>
                            <button onClick={() => mark(s.key)}
                                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${isDone ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-500"}`}
                                    data-testid={`onboarding-mark-${s.key}`}>
                                {isDone ? <CheckCircle2 size={13} /> : i + 1}
                            </button>
                            <div className="flex-1">
                                <div className={`text-[13.5px] font-semibold ${isDone ? "text-stone-400 line-through" : "text-stone-900"}`}>
                                    {s.label}
                                </div>
                            </div>
                            <Link to={s.to}
                                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-stone-700 hover:text-stone-900 border border-stone-300 rounded-full px-3 py-1"
                                  data-testid={`onboarding-goto-${s.key}`}>
                                Open <ChevronRight size={11} />
                            </Link>
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[11.5px] text-stone-500">
                    Skip / continue later is always available — no step is mandatory.
                </div>
                <div className="flex gap-2">
                    <button onClick={skipAll}
                            className="text-[12px] font-semibold text-stone-700 border border-stone-300 rounded-full px-3.5 py-1.5"
                            data-testid="onboarding-skip">
                        Skip everything
                    </button>
                    <Link to="/household"
                          className="text-[12px] font-semibold text-white bg-stone-900 rounded-full px-3.5 py-1.5 inline-flex items-center gap-1"
                          data-testid="onboarding-done">
                        Back to Overview <ArrowRight size={11} />
                    </Link>
                </div>
            </div>
        </div>
    );
}
